<script setup>
import { ref, shallowRef, computed, onMounted, watch, defineAsyncComponent } from 'vue'
import { useRouter } from 'vue-router'
import { getWidgetComponent, getWidgetType } from './widgetRegistry'
import { useGlobalDashboardSettings } from '@/composables/useGlobalDashboardSettings'
import { clampDashboardTimeRange } from '@/config/dashboardOptions'

const router = useRouter()

// Time range to milliseconds mapping
const TIME_RANGE_MS = {
  '1h': 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
  '180d': 180 * 24 * 60 * 60 * 1000,
  '365d': 365 * 24 * 60 * 60 * 1000
}

// Global dashboard settings
const {
  getEffectiveTimeRange,
  getEffectiveRefreshInterval,
  hasTimeRangeOverride,
  hasRefreshOverride
} = useGlobalDashboardSettings()

const props = defineProps({
  widget: {
    type: Object,
    required: true,
    validator: (value) => {
      return value && value.id && value.widget_type
    }
  },
  isLocked: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['configure', 'delete', 'refresh'])

// Component state
const isLoading = ref(true)
const hasError = ref(false)
const errorMessage = ref('')
const widgetComponent = shallowRef(null)
const widgetRef = ref(null)

// Computed: check if widget is refreshing via exposed state
const isWidgetRefreshing = computed(() => {
  return widgetRef.value?.isRefreshing ?? false
})

// Check if widget has a query to enable "Open in Event Search" button
const hasQuery = computed(() => {
  return !!(props.widget.config?.query || props.widget.saved_search?.query)
})

// Computed properties
const widgetTitle = computed(() => {
  return props.widget.config?.title || getWidgetType(props.widget.widget_type)?.name || 'Widget'
})

const widgetType = computed(() => {
  return getWidgetType(props.widget.widget_type)
})

const widgetIcon = computed(() => {
  return widgetType.value?.icon || 'cube'
})

// Create effective config with global overrides applied
const effectiveConfig = computed(() => {
  const config = { ...props.widget.config }

  // Backward compat: old widgets store query in saved_search, not config
  if (!config.query && props.widget.saved_search?.query) {
    config.query = props.widget.saved_search.query
  }

  // Resolve + hard-cap the time range: apply the global override when active,
  // then clamp any stored/legacy value to the 3-month dashboard maximum so no
  // widget can ever query further back than the cap.
  if (config.timeRange !== undefined) {
    config.timeRange = clampDashboardTimeRange(
      hasTimeRangeOverride.value ? getEffectiveTimeRange(config.timeRange) : config.timeRange
    )
  }

  // Apply global refresh interval override if active
  if (config.refreshInterval !== undefined && hasRefreshOverride.value) {
    config.refreshInterval = getEffectiveRefreshInterval(config.refreshInterval)
  }

  return config
})

// Check if any overrides are active for this widget
const hasActiveOverrides = computed(() => {
  const config = props.widget.config || {}
  return (hasTimeRangeOverride.value && config.timeRange !== undefined) ||
         (hasRefreshOverride.value && config.refreshInterval !== undefined)
})

// Check if header should be hidden (only when locked)
const shouldHideHeader = computed(() => {
  return props.isLocked && effectiveConfig.value.hideHeader === true
})

// Load the widget component dynamically
async function loadWidgetComponent() {
  isLoading.value = true
  hasError.value = false
  errorMessage.value = ''

  try {
    const componentLoader = getWidgetComponent(props.widget.widget_type)

    if (!componentLoader) {
      throw new Error(`Unknown widget type: ${props.widget.widget_type}`)
    }

    // Create async component with loading and error handling
    widgetComponent.value = defineAsyncComponent({
      loader: componentLoader,
      loadingComponent: null, // We handle loading ourselves
      errorComponent: null, // We handle errors ourselves
      delay: 0,
      timeout: 30000,
      onError(error, retry, fail, attempts) {
        if (attempts < 3) {
          retry()
        } else {
          fail()
        }
      }
    })

    isLoading.value = false
  } catch (error) {
    console.error('Failed to load widget component:', error)
    hasError.value = true
    errorMessage.value = error.message || 'Failed to load widget'
    isLoading.value = false
  }
}

// Retry loading the widget
function retryLoad() {
  loadWidgetComponent()
}

// Handle refresh - call widget's refresh method if available
function handleRefresh() {
  emit('refresh')
  // Call widget's refresh method if exposed
  if (widgetRef.value?.refresh) {
    widgetRef.value.refresh()
  }
  // Also reload the component if there was an error
  if (hasError.value) {
    loadWidgetComponent()
  }
}

// Open the widget query in Event Search page
function openInEventSearch() {
  const ec = effectiveConfig.value
  if (!ec.query) return

  const query = { query: ec.query }

  // Get effective time range (with overrides applied)
  const timeRange = ec.timeRange

  // Add time filter parameter if time range is configured and not 'all'
  if (timeRange && timeRange !== 'all') {
    const rangeMs = TIME_RANGE_MS[timeRange]
    if (rangeMs) {
      const now = Date.now()
      const start = now - rangeMs
      query.start = start.toString()
      query.timePreset = timeRange
    }
  }

  // Add log type parameter if widget has a single log type configured
  if (ec.logType && typeof ec.logType === 'string') {
    query.logType = ec.logType
  } else if (Array.isArray(ec.logTypes) && ec.logTypes.length === 1) {
    query.logType = ec.logTypes[0]
  }

  // Pass sources if configured
  if (ec.sources) {
    query.sources = ec.sources
  }

  router.push({
    path: '/search/event',
    query
  })
}

// Load component on mount
onMounted(() => {
  loadWidgetComponent()
})

// Reload if widget type changes
watch(() => props.widget.widget_type, () => {
  loadWidgetComponent()
})
</script>

<template>
  <div class="widget-container">
    <!-- Widget Header -->
    <div v-if="!shouldHideHeader" class="widget-header" :class="{ 'is-locked': isLocked }">
      <div class="header-left">
        <font-awesome-icon :icon="widgetIcon" class="widget-icon" />
        <span class="widget-title">{{ widgetTitle }}</span>
        <div v-if="hasActiveOverrides" class="override-badges">
          <span
            v-if="hasTimeRangeOverride && widget.config?.timeRange !== undefined"
            class="override-badge"
            title="Using global time range"
          >
            <font-awesome-icon icon="clock" class="badge-icon" />
          </span>
          <span
            v-if="hasRefreshOverride && widget.config?.refreshInterval !== undefined"
            class="override-badge"
            title="Using global refresh interval"
          >
            <font-awesome-icon icon="arrows-rotate" class="badge-icon" />
          </span>
        </div>
      </div>

      <div class="header-actions">
        <button
          v-if="hasQuery"
          class="btn-action btn-hover-only"
          @click="openInEventSearch"
          title="Open in Event Search"
        >
          <font-awesome-icon icon="arrow-up-right-from-square" />
        </button>

        <button
          class="btn-action btn-hover-only"
          :disabled="isWidgetRefreshing"
          @click="handleRefresh"
          title="Refresh"
        >
          <font-awesome-icon icon="arrows-rotate" :class="{ 'is-refreshing': isWidgetRefreshing }" />
        </button>

        <button
          v-if="!isLocked"
          class="btn-action"
          @click="emit('configure')"
          title="Settings"
        >
          <font-awesome-icon icon="cog" />
        </button>

        <button
          v-if="!isLocked"
          class="btn-action btn-delete"
          @click="emit('delete')"
          title="Delete"
        >
          <font-awesome-icon icon="trash" />
        </button>
      </div>
    </div>

    <!-- Widget Content -->
    <div class="widget-content">
      <!-- Loading State -->
      <div v-if="isLoading" class="widget-loading">
        <font-awesome-icon icon="spinner" spin class="loading-spinner" />
        <span class="loading-text">Loading widget...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="hasError" class="widget-error">
        <font-awesome-icon icon="exclamation-triangle" class="error-icon" />
        <span class="error-message">{{ errorMessage }}</span>
        <button class="btn-retry" @click="retryLoad">
          <font-awesome-icon icon="redo" />
          Retry
        </button>
      </div>

      <!-- Widget Component -->
      <component
        v-else-if="widgetComponent"
        ref="widgetRef"
        :is="widgetComponent"
        :widget="widget"
        :config="effectiveConfig"
        class="widget-inner"
      />
    </div>
  </div>
</template>

<style scoped>
.widget-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: transparent;
}

/* Widget Header */
.widget-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: transparent;
  cursor: grab;
  user-select: none;
}

.widget-header:active {
  cursor: grabbing;
}

.widget-header.is-locked {
  cursor: default;
}

.widget-header.is-locked:active {
  cursor: default;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
  flex: 1;
}

.widget-icon {
  font-size: 0.875rem;
  color: var(--accent);
  flex-shrink: 0;
}

.widget-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

/* Hide refresh and open-search buttons until widget is hovered */
.header-actions .btn-action.btn-hover-only {
  opacity: 0;
  transition: opacity 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.widget-container:hover .header-actions .btn-action.btn-hover-only {
  opacity: 1;
}

.btn-action {
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-faint);
  font-size: 0.75rem;
  cursor: pointer;
  transition: var(--transition);
}

.btn-action:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary);
}

.btn-action.btn-delete:hover {
  background: rgba(239, 68, 68, 0.2);
  color: var(--color-error);
}

/* Refresh icon rotation animation */
.is-refreshing {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Widget Content */
.widget-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
}

/* Full height when header is hidden */
.widget-container:not(:has(.widget-header)) {
  height: 100%;
}

.widget-container:not(:has(.widget-header)) .widget-content {
  height: 100%;
}

.widget-inner {
  width: 100%;
  height: 100%;
}

/* Loading State */
.widget-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  gap: 0.75rem;
}

.loading-spinner {
  font-size: 1.5rem;
  color: var(--accent);
}

.loading-text {
  font-size: 0.8125rem;
  color: var(--text-muted);
}

/* Error State */
.widget-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  gap: 0.75rem;
  text-align: center;
}

.error-icon {
  font-size: 1.5rem;
  color: var(--color-warning);
}

.error-message {
  font-size: 0.8125rem;
  color: var(--text-muted);
  max-width: 200px;
}

.btn-retry {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--accent-soft-3);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: var(--radius-sm);
  color: var(--accent);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
}

.btn-retry:hover {
  background: var(--accent-soft-4);
  border-color: rgba(115, 87, 210, 0.5);
}

/* Override Badges */
.override-badges {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: 0.25rem;
}

.override-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  background: rgba(139, 92, 246, 0.15);
  border-radius: var(--radius-xs);
  cursor: help;
  flex-shrink: 0;
}

.badge-icon {
  width: 0.75rem;
  height: 0.75rem;
  color: var(--accent-text);
}
</style>
